import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  activeTab: null,
  watchlistData: {},
  loading: false,
  error: null,
  marketWatchListData: {},
  // Last-known market rows per tab ({ NFO: [...], MCX: [...] }). Lets a tab
  // switch paint instantly from cache while the fresh prices load in the
  // background — no shimmer on switch.
  marketDataCache: {},
  selectedItem: null,
};

const watchlistSlice = createSlice({
  name: 'watchlist',
  initialState,
  reducers: {
    // Fetch watchlist actions
    fetchWatchlistRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchWatchlistSuccess: (state, action) => {
      state.loading = false;
      state.watchlistData = action.payload;
    },

    fetchWatchlistFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchMarketForWatchListRequest: (state) => {
      state.loading = true;
      state.error = null;
    },

    fetchMarketForWatchListSuccess: (state, action) => {
      state.loading = false;
      state.marketWatchListData = action.payload;
      // Warm the per-tab cache so switching back to this tab is instant.
      if (state.activeTab && Array.isArray(action.payload)) {
        state.marketDataCache[state.activeTab] = action.payload;
      }
    },
    fetchMarketForWatchListFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Delete watchlist actions
    deleteWatchlistRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteWatchlistSuccess: (state, action) => {
      state.loading = false;
      // Invalidate the cached watchlist so the follow-up fetchWatchlistRequest
      // (dispatched by the delete saga) re-hits REST and returns a fresh list
      // without the removed item — rather than reusing the now-stale cache.
      state.watchlistData = {};
    },

    deleteWatchlistFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Set active tab
    setActiveTab: (state, action) => {
      const nextTab = action.payload;

      // ── OLD (showed a shimmer on every tab switch). Kept commented for an
      //    easy revert if the cache approach ever needs to be rolled back. ──
      // if (state.activeTab !== nextTab) {
      //   state.marketWatchListData = [];
      //   state.loading = true;
      // }
      // state.activeTab = nextTab;

      // ── NEW: instant switch, NO shimmer. Paint the tab's last-known (cached)
      //    rows immediately so the change feels instant; the fresh market call
      //    updates the prices 1–2s later in the background. A tab that was
      //    never opened this session simply starts from an empty cache. ──
      if (state.activeTab !== nextTab) {
        state.marketWatchListData = state.marketDataCache[nextTab] || [];
      }
      state.activeTab = nextTab;
    },

    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload;
    },
    clearSelectedItem: (state) => {
      state.selectedItem = null;
    },
    updateMarketWatchListData: (state, action) => {
      state.marketWatchListData = action.payload;
      // Keep the per-tab cache in sync with live socket / poll updates too.
      if (state.activeTab && Array.isArray(action.payload)) {
        state.marketDataCache[state.activeTab] = action.payload;
      }
    },
    // Clear watchlist
    clearWatchlist: (state) => {
      state.watchlistData = {};
      state.marketWatchListData = [];
      state.marketDataCache = {};
      state.availableTabs = [];
      state.activeTab = 'MCX';
    },
  },
});

export const {
  fetchWatchlistRequest,
  fetchWatchlistSuccess,
  fetchWatchlistFailure,
  fetchMarketForWatchListRequest,
  fetchMarketForWatchListSuccess,
  fetchMarketForWatchListFailure,
  deleteWatchlistRequest,
  deleteWatchlistSuccess,
  deleteWatchlistFailure,
  setActiveTab,
  clearWatchlist,
  setSelectedItem,
  clearSelectedItem,
  updateMarketWatchListData
} = watchlistSlice.actions;

export default watchlistSlice.reducer;